/**
 * Polygon topology utilities.
 *
 * All functions are pure and operate on the PolygonTopology structure
 * (face offsets + corner vertices), the same representation Blender uses.
 *
 * Triangle indices for GPU rendering are always derived from topology
 * via fan triangulation — topology is the single source of truth for
 * face structure.
 */

import { buildTopologyEdges } from "../wasm/topology.ts";
import {
  asRenderTriangleIndices,
  asSharedTriangleIndices,
  type RenderTriangleIndices,
  type SharedTriangleIndices,
} from "./triangleIndexTypes.ts";
import type { PolygonTopology } from "./types.ts";
import { EDGE_SEAM, EDGE_SHARP, EDGE_WIRE } from "./types.ts";

// ---------------------------------------------------------------------------
// Edge construction
// ---------------------------------------------------------------------------

/**
 * Build explicit edge arrays from face topology.
 *
 * Returns `{ edges, cornerEdges, edgeFlags }` matching the PolygonTopology
 * fields.  Delegates to the Rust `build_edges_from_topology` via the WASM
 * wrapper in `wasm/topology.ts`, eliminating the O(E) `Map<number,number>`
 * allocation that the previous TypeScript mirror incurred.
 */
export function buildEdgesAndCornerEdges(
  offsets: Uint32Array,
  corners: Uint32Array,
): { edges: Uint32Array; cornerEdges: Uint32Array; edgeFlags: Uint8Array } {
  return buildTopologyEdges(offsets, corners);
}

/**
 * Identity remap — pass as `remapVertex` to
 * `rebuildTopologyEdgesPreservingWire` when the operation leaves vertex
 * indices unchanged (e.g. flip normals, append face).
 */
export const identityRemap = (v: number): number => v;

const EDGE_PROPAGATE_MASK = EDGE_SHARP | EDGE_SEAM;

function mergeOldEdgeFlags(
  edges: Uint32Array,
  edgeFlags: Uint8Array,
  sourceTopo: Pick<PolygonTopology, "edges" | "edgeFlags">,
): void {
  const oldCount = sourceTopo.edges.length / 2;
  const oldFlagMap = new Map<number, number>();

  for (let i = 0; i < oldCount; i++) {
    const propagated = sourceTopo.edgeFlags[i] & EDGE_PROPAGATE_MASK;
    if (propagated === 0) continue;
    oldFlagMap.set(
      numEdgeKey(sourceTopo.edges[i * 2], sourceTopo.edges[i * 2 + 1]),
      propagated,
    );
  }

  const edgeCount = edges.length / 2;
  for (let i = 0; i < edgeCount; i++) {
    const propagated =
      oldFlagMap.get(numEdgeKey(edges[i * 2], edges[i * 2 + 1])) ?? 0;
    edgeFlags[i] |= propagated & EDGE_PROPAGATE_MASK;
  }
}

/**
 * Project source `edgeCreases` onto a rebuilt edge array by canonical-key
 * lookup. Returns `undefined` when the source carries no creases or when
 * every projected weight is 0 — preserving the "omit-when-empty" invariant
 * on `PolygonTopology.edgeCreases`.
 *
 * This is the non-vertex-remapping counterpart to `remapEdgeCreases` in
 * `provenance.ts`: it assumes the new edges reference the SAME vertex
 * indices as the source (i.e. the op that rebuilt edges did not
 * renumber / split / merge vertices). Used by
 * `rebuildTopologyEdgesPreservingWire` — every op that renumbers vertices
 * also produces a `VertexProvenance` and flows through `applyResult`,
 * which runs the provenance-based remap and overwrites whatever this
 * helper produced.
 */
function propagateEdgeCreases(
  newEdges: Uint32Array,
  sourceTopo: PolygonTopology,
): Float32Array | undefined {
  const src = sourceTopo.edgeCreases;
  if (!src || src.length === 0) return undefined;
  const oldCount = sourceTopo.edges.length / 2;

  const oldCreaseMap = new Map<number, number>();
  for (let i = 0; i < oldCount; i++) {
    const c = src[i];
    if (c === 0) continue;
    oldCreaseMap.set(
      numEdgeKey(sourceTopo.edges[i * 2], sourceTopo.edges[i * 2 + 1]),
      c,
    );
  }
  if (oldCreaseMap.size === 0) return undefined;

  const edgeCount = newEdges.length / 2;
  const out = new Float32Array(edgeCount);
  let anyNonZero = false;
  for (let i = 0; i < edgeCount; i++) {
    const c = oldCreaseMap.get(
      numEdgeKey(newEdges[i * 2], newEdges[i * 2 + 1]),
    );
    if (c !== undefined) {
      out[i] = c;
      anyNonZero = true;
    }
  }
  return anyNonZero ? out : undefined;
}

/**
 * Rebuild the edge arrays for a topology whose face corners have been
 * transformed (filtered, remapped, reordered, or all three), preserving
 * wire edges from the source wherever both endpoints survive.
 *
 * Use this INSTEAD of `buildEdgesAndCornerEdges` for any operation that
 * mutates an existing topology. `buildEdgesAndCornerEdges` alone derives
 * edges strictly from face corners and therefore silently destroys wire
 * edges on every rebuild — a consistent source of bugs across the
 * codebase (delete faces, flip normals, separate, clean up, append face,
 * bridge vertices, extract sub-topology for duplicate, etc.).
 *
 * Source edges — face-bound AND wire — are walked and any whose both
 * endpoints still exist (as reported by `remapVertex`) are kept:
 *
 *   • If the remapped endpoints already correspond to a face-bound edge
 *     in the new topology (same canonical key), the edge is dropped as
 *     a duplicate — the face pass already emitted it.
 *   • Otherwise the edge is appended as a wire edge (`EDGE_WIRE`).
 *
 * Edges whose remap drops an endpoint (< 0) or collapses both endpoints
 * to the same vertex are discarded.
 *
 * @param newOffsets   Face offsets of the new topology.
 * @param newCorners   Face corners of the new topology (already mapped
 *                     into the new vertex index space).
 * @param sourceTopo   The topology being transformed — its `edges` and
 *                     `edgeFlags` are the source of wire-edge carry-over.
 * @param remapVertex  Maps a source vertex index to its new index, or
 *                     returns -1 / any negative number to indicate the
 *                     vertex does not exist in the new topology. Pass
 *                     the identity `(v) => v` when vertex indices are
 *                     unchanged (e.g. flip normals).
 */
export function rebuildTopologyEdgesPreservingWire(
  newOffsets: Uint32Array,
  newCorners: Uint32Array,
  sourceTopo: PolygonTopology,
  remapVertex: (old: number) => number,
): PolygonTopology {
  const face = buildTopologyEdges(newOffsets, newCorners);

  // Index the face-derived edges by canonical key so we can dedup source
  // edges that have already been re-emitted by the face pass.
  const faceEdgeKeys = new Set<number>();
  for (let i = 0; i < face.edges.length; i += 2) {
    faceEdgeKeys.add(numEdgeKey(face.edges[i], face.edges[i + 1]));
  }

  const { edges: srcEdges } = sourceTopo;
  const srcEdgeCount = srcEdges.length / 2;
  const extraWireKeys = new Set<number>();
  const extraWireVerts: number[] = [];

  for (let i = 0; i < srcEdgeCount; i++) {
    const v0 = srcEdges[i * 2];
    const v1 = srcEdges[i * 2 + 1];
    const nv0 = remapVertex(v0);
    const nv1 = remapVertex(v1);
    if (nv0 < 0 || nv1 < 0) continue;
    if (nv0 === nv1) continue;
    const key = numEdgeKey(nv0, nv1);
    if (faceEdgeKeys.has(key)) continue;
    if (extraWireKeys.has(key)) continue;
    extraWireKeys.add(key);
    extraWireVerts.push(nv0, nv1);
  }

  let outEdges = face.edges;
  let outFlags = face.edgeFlags;

  if (extraWireVerts.length > 0) {
    const wireCount = extraWireVerts.length / 2;
    const mergedEdges = new Uint32Array(
      face.edges.length + extraWireVerts.length,
    );
    mergedEdges.set(face.edges);
    for (let i = 0; i < extraWireVerts.length; i++) {
      mergedEdges[face.edges.length + i] = extraWireVerts[i];
    }

    const mergedFlags = new Uint8Array(face.edgeFlags.length + wireCount);
    mergedFlags.set(face.edgeFlags);
    for (let i = 0; i < wireCount; i++) {
      mergedFlags[face.edgeFlags.length + i] = EDGE_WIRE;
    }

    outEdges = mergedEdges;
    outFlags = mergedFlags;
  }

  mergeOldEdgeFlags(outEdges, outFlags, sourceTopo);

  // Per-edge crease weights travel the same canonical-key path as sharp /
  // seam flags. Omitted when the source had none or every projected
  // weight would be 0.
  const edgeCreases = propagateEdgeCreases(outEdges, sourceTopo);

  return {
    offsets: newOffsets,
    corners: newCorners,
    edges: outEdges,
    cornerEdges: face.cornerEdges,
    edgeFlags: outFlags,
    ...(edgeCreases ? { edgeCreases } : {}),
  };
}

/**
 * Rebuild a topology's explicit edge tables into canonical form while
 * preserving every per-edge attribute the source carries (wire edges,
 * SHARP / SEAM flags, crease weights).
 */
export function canonicalizeTopology(topo: PolygonTopology): PolygonTopology {
  return rebuildTopologyEdgesPreservingWire(
    new Uint32Array(topo.offsets),
    new Uint32Array(topo.corners),
    topo,
    identityRemap,
  );
}

/**
 * Build a full `PolygonTopology` from face corners plus an explicit edge set
 * that already lives in the output vertex space.
 *
 * Use this when an operation can directly enumerate its output edges
 * (including wires) but still needs a coherent `cornerEdges` table for the
 * polygon faces. Face edges are derived from `offsets/corners`; explicit edge
 * metadata is then merged onto them by canonical key, and any remaining
 * non-face edges are appended as wires.
 */
export function buildTopologyWithExplicitEdges(
  offsets: Uint32Array,
  corners: Uint32Array,
  explicitEdges: Uint32Array,
  explicitFlags: Uint8Array,
  explicitCreases?: Float32Array | null,
): PolygonTopology {
  const face = buildEdgesAndCornerEdges(offsets, corners);
  const faceEdgeCount = face.edges.length / 2;

  const faceEdgeMap = new Map<number, number>();
  for (let i = 0; i < faceEdgeCount; i++) {
    faceEdgeMap.set(numEdgeKey(face.edges[i * 2], face.edges[i * 2 + 1]), i);
  }

  const extraEdgeMap = new Map<number, number>();
  const extraEdges: number[] = [];
  const extraFlags: number[] = [];
  const extraCreases: number[] = [];

  const faceFlags = new Uint8Array(face.edgeFlags);
  const faceCreases = explicitCreases ? new Float32Array(faceEdgeCount) : null;
  let anyNonZeroCrease = false;

  const explicitEdgeCount = explicitEdges.length / 2;
  for (let i = 0; i < explicitEdgeCount; i++) {
    const v0 = explicitEdges[i * 2];
    const v1 = explicitEdges[i * 2 + 1];
    const key = numEdgeKey(v0, v1);
    const flag = explicitFlags[i];
    const crease = explicitCreases?.[i] ?? 0;

    const faceEdgeIndex = faceEdgeMap.get(key);
    if (faceEdgeIndex !== undefined) {
      faceFlags[faceEdgeIndex] |= flag & ~EDGE_WIRE;
      if (faceCreases && crease !== 0) {
        faceCreases[faceEdgeIndex] = Math.max(
          faceCreases[faceEdgeIndex],
          crease,
        );
        anyNonZeroCrease = true;
      }
      continue;
    }

    const extraIndex = extraEdgeMap.get(key);
    if (extraIndex !== undefined) {
      extraFlags[extraIndex] |= flag | EDGE_WIRE;
      if (explicitCreases && crease !== 0) {
        extraCreases[extraIndex] = Math.max(
          extraCreases[extraIndex] ?? 0,
          crease,
        );
        anyNonZeroCrease = true;
      }
      continue;
    }

    extraEdgeMap.set(key, extraEdges.length / 2);
    extraEdges.push(v0, v1);
    extraFlags.push(flag | EDGE_WIRE);
    if (explicitCreases) {
      extraCreases.push(crease);
      if (crease !== 0) anyNonZeroCrease = true;
    }
  }

  const extraEdgeCount = extraEdges.length / 2;
  const edges = new Uint32Array(face.edges.length + extraEdges.length);
  edges.set(face.edges);
  if (extraEdges.length > 0) edges.set(extraEdges, face.edges.length);

  const edgeFlags = new Uint8Array(faceEdgeCount + extraEdgeCount);
  edgeFlags.set(faceFlags);
  if (extraEdgeCount > 0) edgeFlags.set(extraFlags, faceEdgeCount);

  let edgeCreases: Float32Array | undefined;
  if (explicitCreases && anyNonZeroCrease) {
    const merged = new Float32Array(faceEdgeCount + extraEdgeCount);
    if (faceCreases) merged.set(faceCreases);
    if (extraEdgeCount > 0) merged.set(extraCreases, faceEdgeCount);
    edgeCreases = merged;
  }

  return {
    offsets,
    corners,
    edges,
    cornerEdges: face.cornerEdges,
    edgeFlags,
    ...(edgeCreases ? { edgeCreases } : {}),
  };
}

/**
 * Deep-clone a `PolygonTopology`. Every typed-array field is copied (even
 * the ones most callers treat as immutable) so a mutation in one place
 * can't bleed across references — undo snapshots depend on this. Every
 * optional per-edge attribute is included by construction, so new fields
 * added to `PolygonTopology` flow through without edits here.
 *
 * Centralised: any site that needs "a fresh, independent topology" should
 * call this helper rather than reassembling `{ offsets, corners, ... }`
 * inline. That's the invariant that keeps per-edge attributes (flags,
 * creases, future weights) from silently dropping through clone paths.
 */
export function cloneTopology(topo: PolygonTopology): PolygonTopology {
  return {
    offsets: new Uint32Array(topo.offsets),
    corners: new Uint32Array(topo.corners),
    edges: new Uint32Array(topo.edges),
    cornerEdges: new Uint32Array(topo.cornerEdges),
    edgeFlags: new Uint8Array(topo.edgeFlags),
    ...(topo.edgeCreases
      ? { edgeCreases: new Float32Array(topo.edgeCreases) }
      : {}),
  };
}

/**
 * Structural equality for `PolygonTopology`. Every field — required or
 * optional — must match in both presence and content. Undefined optionals
 * on one side and a defined array on the other return false (matching the
 * omit-when-empty invariant: a missing field is NOT the same shape as
 * an explicitly-zeroed array).
 *
 * Centralised alongside `cloneTopology` so every per-edge attribute stays
 * in lockstep with its clone/compare behaviour.
 */
export function equalTopology(a: PolygonTopology, b: PolygonTopology): boolean {
  if (a === b) return true;
  if (!equalTypedArray(a.offsets, b.offsets)) return false;
  if (!equalTypedArray(a.corners, b.corners)) return false;
  if (!equalTypedArray(a.edges, b.edges)) return false;
  if (!equalTypedArray(a.cornerEdges, b.cornerEdges)) return false;
  if (!equalTypedArray(a.edgeFlags, b.edgeFlags)) return false;
  if (!equalOptionalTypedArray(a.edgeCreases, b.edgeCreases)) return false;
  return true;
}

function equalTypedArray(a: ArrayLike<number>, b: ArrayLike<number>): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function equalOptionalTypedArray(
  a: ArrayLike<number> | undefined,
  b: ArrayLike<number> | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return equalTypedArray(a, b);
}

/**
 * Extract wire edge vertex pairs from a topology's edges + edgeFlags.
 * Returns flat [v0, v1, v0, v1, ...] for wire edges only.
 */
export function getWireEdgePairs(topo: PolygonTopology): Uint32Array {
  const { edges, edgeFlags } = topo;
  const pairs: number[] = [];
  const edgeCount = edges.length / 2;
  for (let i = 0; i < edgeCount; i++) {
    if (edgeFlags[i] & EDGE_WIRE) {
      pairs.push(edges[i * 2], edges[i * 2 + 1]);
    }
  }
  return new Uint32Array(pairs);
}

// ---------------------------------------------------------------------------
// Construction
// ---------------------------------------------------------------------------

/** Create a trivial topology where each triangle is its own face. */
export function topologyFromTriangles(
  indices: SharedTriangleIndices,
): PolygonTopology {
  if (import.meta.env.DEV && indices.length % 3 !== 0) {
    throw new Error(
      `topologyFromTriangles: index count ${indices.length} is not divisible by 3`,
    );
  }
  const faceCount = indices.length / 3;
  const offsets = new Uint32Array(faceCount + 1);
  for (let i = 0; i <= faceCount; i++) offsets[i] = i * 3;
  const cornersCopy = new Uint32Array(indices);
  const { edges, cornerEdges, edgeFlags } = buildEdgesAndCornerEdges(
    offsets,
    cornersCopy,
  );
  return { offsets, corners: cornersCopy, edges, cornerEdges, edgeFlags };
}

// ---------------------------------------------------------------------------
// Triangulation
// ---------------------------------------------------------------------------

/** Fan-triangulate all polygon faces into a shared-vertex triangle index buffer.
 *  Also appends degenerate triangles [a, b, a] for wire edges. */
export function triangulateTopology(
  topo: PolygonTopology,
): SharedTriangleIndices {
  const { offsets, corners, edges, edgeFlags } = topo;
  const faceCount = offsets.length - 1;

  // Count wire edges
  const edgeCount = edges.length / 2;
  let wireCount = 0;
  for (let i = 0; i < edgeCount; i++) {
    if (edgeFlags[i] & EDGE_WIRE) wireCount++;
  }

  // Count total triangles first for a single allocation
  let triCount = 0;
  for (let f = 0; f < faceCount; f++) {
    const size = offsets[f + 1] - offsets[f];
    if (size >= 3) triCount += size - 2;
  }
  triCount += wireCount;

  const out = new Uint32Array(triCount * 3);
  let w = 0;
  for (let f = 0; f < faceCount; f++) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    const v0 = corners[start];
    for (let j = 1; j < size - 1; j++) {
      out[w++] = v0;
      out[w++] = corners[start + j];
      out[w++] = corners[start + j + 1];
    }
  }
  // Degenerate triangles for wire edges (zero-area, invisible to renderer)
  for (let i = 0; i < edgeCount; i++) {
    if (edgeFlags[i] & EDGE_WIRE) {
      out[w++] = edges[i * 2];
      out[w++] = edges[i * 2 + 1];
      out[w++] = edges[i * 2];
    }
  }
  return asSharedTriangleIndices(out);
}

/**
 * Triangulate topology for rendering — returns CORNER indices (0..totalCorners-1)
 * instead of vertex indices. These index into expanded per-corner arrays
 * produced by `expandForRendering()`.
 *
 * This is distinct from `triangulateTopology()` which returns shared vertex indices
 * (used by geometry operators for topology queries).
 */
export function triangulateTopologyForRendering(
  topo: PolygonTopology,
): RenderTriangleIndices {
  const { offsets } = topo;
  const faceCount = offsets.length - 1;

  let triCount = 0;
  for (let f = 0; f < faceCount; f++) {
    const size = offsets[f + 1] - offsets[f];
    if (size >= 3) triCount += size - 2;
  }

  const out = new Uint32Array(triCount * 3);
  let w = 0;
  for (let f = 0; f < faceCount; f++) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    // Fan triangulation using corner indices (not vertex indices)
    for (let j = 1; j < size - 1; j++) {
      out[w++] = start; // corner index of first vertex
      out[w++] = start + j; // corner index of j-th vertex
      out[w++] = start + j + 1; // corner index of (j+1)-th vertex
    }
  }
  return asRenderTriangleIndices(out);
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** Number of polygon faces. */
export function topologyFaceCount(topo: PolygonTopology): number {
  return topo.offsets.length - 1;
}

/** Vertex indices for polygon face `faceIndex`. */
export function topologyFaceVerts(
  topo: PolygonTopology,
  faceIndex: number,
): number[] {
  if (import.meta.env.DEV) {
    const faceCount = topo.offsets.length - 1;
    if (faceIndex < 0 || faceIndex >= faceCount) {
      throw new Error(
        `topologyFaceVerts: faceIndex ${faceIndex} out of bounds (faceCount=${faceCount})`,
      );
    }
  }
  const start = topo.offsets[faceIndex];
  const end = topo.offsets[faceIndex + 1];
  const verts: number[] = [];
  for (let i = start; i < end; i++) verts.push(topo.corners[i]);
  return verts;
}

// ---------------------------------------------------------------------------
// Edge utilities
// ---------------------------------------------------------------------------

/** Multiplier for numeric edge keys. 2^26 supports up to ~67M vertices per
 *  component while keeping the packed key within Number.MAX_SAFE_INTEGER. */
const NUM_EDGE_MUL = 67108864; // 2^26

/** Numeric edge key — canonical, order-independent. Supports up to ~67M vertices. */
export function numEdgeKey(a: number, b: number): number {
  const lo = a < b ? a : b;
  const hi = a < b ? b : a;
  return lo * NUM_EDGE_MUL + hi;
}

// ---------------------------------------------------------------------------
// Edge index lookup
// ---------------------------------------------------------------------------

/** Return the vertex pair [v0, v1] for edge at the given index. */
export function edgeVertices(
  edges: Uint32Array,
  edgeIdx: number,
): [number, number] {
  return [edges[edgeIdx * 2], edges[edgeIdx * 2 + 1]];
}

const edgeIndexCache = new WeakMap<Uint32Array, Map<number, number>>();

/** Build or retrieve a cached numEdgeKey -> edgeIndex map for the given edges array. */
function getEdgeIndexMap(edges: Uint32Array): Map<number, number> {
  let map = edgeIndexCache.get(edges);
  if (!map) {
    map = new Map<number, number>();
    const edgeCount = edges.length / 2;
    for (let i = 0; i < edgeCount; i++) {
      map.set(numEdgeKey(edges[i * 2], edges[i * 2 + 1]), i);
    }
    edgeIndexCache.set(edges, map);
  }
  return map;
}

/** Find the edge index for a vertex pair, or -1 if not found. */
export function findEdgeIndex(
  topo: PolygonTopology,
  v0: number,
  v1: number,
): number {
  const map = getEdgeIndexMap(topo.edges);
  return map.get(numEdgeKey(v0, v1)) ?? -1;
}

// ---------------------------------------------------------------------------
// Edge flag mutations — copy-on-write clones of topology.edgeFlags
// ---------------------------------------------------------------------------

/**
 * Produce a new topology with `mask` OR'd into `edgeFlags` at every index in
 * `edgeIndices`. Returns the input topology unchanged when no bits would
 * actually flip, so callers can diff for "nothing to do" without a second
 * pass. All other topology arrays are shared (they are immutable after build).
 */
export function withEdgeFlagsSet(
  topo: PolygonTopology,
  edgeIndices: Iterable<number>,
  mask: number,
): PolygonTopology {
  const src = topo.edgeFlags;
  let next: Uint8Array | null = null;
  for (const ei of edgeIndices) {
    if (ei < 0 || ei >= src.length) continue;
    if ((src[ei] & mask) === mask) continue;
    if (!next) next = new Uint8Array(src);
    next[ei] |= mask;
  }
  return next ? { ...topo, edgeFlags: next } : topo;
}

/**
 * Produce a new topology with `mask` AND-NOT'd out of `edgeFlags` at every
 * index in `edgeIndices`. Returns the input unchanged when no bits flip.
 */
export function withEdgeFlagsCleared(
  topo: PolygonTopology,
  edgeIndices: Iterable<number>,
  mask: number,
): PolygonTopology {
  const src = topo.edgeFlags;
  const inv = ~mask & 0xff;
  let next: Uint8Array | null = null;
  for (const ei of edgeIndices) {
    if (ei < 0 || ei >= src.length) continue;
    if ((src[ei] & mask) === 0) continue;
    if (!next) next = new Uint8Array(src);
    next[ei] &= inv;
  }
  return next ? { ...topo, edgeFlags: next } : topo;
}

/**
 * Produce a new topology with `edgeCreases` replaced (or removed when
 * `creases` is undefined). The "omit-when-empty" invariant on
 * `PolygonTopology.edgeCreases` is enforced here — callers that compute a
 * Float32Array can pass it directly, and passing `undefined` strips the
 * field. Returns the input unchanged when the field would not actually
 * change.
 */
export function withEdgeCreases(
  topo: PolygonTopology,
  creases: Float32Array | undefined,
): PolygonTopology {
  if (creases === topo.edgeCreases) return topo;
  if (creases) return { ...topo, edgeCreases: creases };
  if (!topo.edgeCreases) return topo;
  const { edgeCreases: _drop, ...rest } = topo;
  return rest;
}

// ---------------------------------------------------------------------------
// CSR adjacency cache
// ---------------------------------------------------------------------------

/**
 * Compressed Sparse Row adjacency cache for topology queries.
 * Built lazily from the explicit edge arrays and cached per topology object.
 */
export interface AdjacencyCache {
  /** vertEdgeOffsets[v]..vertEdgeOffsets[v+1] indexes into vertEdgeIndices */
  vertEdgeOffsets: Uint32Array;
  vertEdgeIndices: Uint32Array;
  /** edgeFaceOffsets[e]..edgeFaceOffsets[e+1] indexes into edgeFaceIndices */
  edgeFaceOffsets: Uint32Array;
  edgeFaceIndices: Uint32Array;
}

/**
 * Build CSR adjacency from topology's explicit edges + cornerEdges.
 * O(V + E + C) — two counting passes.
 */
export function buildAdjacencyCache(
  topo: PolygonTopology,
  vertexCount: number,
): AdjacencyCache {
  const { edges, cornerEdges, offsets } = topo;
  const edgeCount = edges.length / 2;
  const faceCount = offsets.length - 1;

  // --- vertex → edge CSR ---
  const veCounts = new Uint32Array(vertexCount);
  for (let i = 0; i < edgeCount; i++) {
    veCounts[edges[i * 2]]++;
    veCounts[edges[i * 2 + 1]]++;
  }
  const vertEdgeOffsets = new Uint32Array(vertexCount + 1);
  for (let v = 0; v < vertexCount; v++) {
    vertEdgeOffsets[v + 1] = vertEdgeOffsets[v] + veCounts[v];
  }
  const vertEdgeIndices = new Uint32Array(vertEdgeOffsets[vertexCount]);
  const veFill = new Uint32Array(vertexCount);
  for (let i = 0; i < edgeCount; i++) {
    const v0 = edges[i * 2],
      v1 = edges[i * 2 + 1];
    vertEdgeIndices[vertEdgeOffsets[v0] + veFill[v0]++] = i;
    vertEdgeIndices[vertEdgeOffsets[v1] + veFill[v1]++] = i;
  }

  // --- edge → face CSR ---
  const efCounts = new Uint32Array(edgeCount);
  for (let f = 0; f < faceCount; f++) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    for (let j = 0; j < size; j++) {
      efCounts[cornerEdges[start + j]]++;
    }
  }
  const edgeFaceOffsets = new Uint32Array(edgeCount + 1);
  for (let e = 0; e < edgeCount; e++) {
    edgeFaceOffsets[e + 1] = edgeFaceOffsets[e] + efCounts[e];
  }
  const edgeFaceIndices = new Uint32Array(edgeFaceOffsets[edgeCount]);
  const efFill = new Uint32Array(edgeCount);
  for (let f = 0; f < faceCount; f++) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    for (let j = 0; j < size; j++) {
      const ei = cornerEdges[start + j];
      edgeFaceIndices[edgeFaceOffsets[ei] + efFill[ei]++] = f;
    }
  }

  return { vertEdgeOffsets, vertEdgeIndices, edgeFaceOffsets, edgeFaceIndices };
}

const adjCacheMap = new WeakMap<PolygonTopology, AdjacencyCache>();

/** Get or build the CSR adjacency cache for a topology. */
export function getAdjacencyCache(
  topo: PolygonTopology,
  vertexCount: number,
): AdjacencyCache {
  let cached = adjCacheMap.get(topo);
  if (!cached) {
    cached = buildAdjacencyCache(topo, vertexCount);
    adjCacheMap.set(topo, cached);
  }
  return cached;
}

/** Edge indices touching vertex v (zero-copy subarray view). */
export function vertexEdges(cache: AdjacencyCache, v: number): Uint32Array {
  return cache.vertEdgeIndices.subarray(
    cache.vertEdgeOffsets[v],
    cache.vertEdgeOffsets[v + 1],
  );
}

/** Face indices touching edge e (zero-copy subarray view). */
export function edgeFaces(cache: AdjacencyCache, e: number): Uint32Array {
  return cache.edgeFaceIndices.subarray(
    cache.edgeFaceOffsets[e],
    cache.edgeFaceOffsets[e + 1],
  );
}

/** Vertex indices adjacent to vertex v (allocates small array). */
export function vertexNeighbors(
  cache: AdjacencyCache,
  topo: PolygonTopology,
  v: number,
): number[] {
  const eIndices = vertexEdges(cache, v);
  const neighbors: number[] = [];
  for (let i = 0; i < eIndices.length; i++) {
    const ei = eIndices[i];
    const other =
      topo.edges[ei * 2] === v ? topo.edges[ei * 2 + 1] : topo.edges[ei * 2];
    neighbors.push(other);
  }
  return neighbors;
}

/** Face indices touching vertex v (allocates, deduplicates). */
export function vertexFacesCSR(cache: AdjacencyCache, v: number): number[] {
  const eIndices = vertexEdges(cache, v);
  const faces: number[] = [];
  const seen = new Set<number>();
  for (let i = 0; i < eIndices.length; i++) {
    const fIndices = edgeFaces(cache, eIndices[i]);
    for (let j = 0; j < fIndices.length; j++) {
      if (!seen.has(fIndices[j])) {
        seen.add(fIndices[j]);
        faces.push(fIndices[j]);
      }
    }
  }
  return faces;
}

/** True if edge has exactly 1 adjacent face. */
export function isEdgeBoundary(cache: AdjacencyCache, e: number): boolean {
  return cache.edgeFaceOffsets[e + 1] - cache.edgeFaceOffsets[e] === 1;
}

/** True if edge has exactly 2 adjacent faces. */
export function isEdgeManifold(cache: AdjacencyCache, e: number): boolean {
  return cache.edgeFaceOffsets[e + 1] - cache.edgeFaceOffsets[e] === 2;
}

/** Face indices adjacent to face f (share an edge). Allocates. */
export function faceNeighbors(
  cache: AdjacencyCache,
  topo: PolygonTopology,
  f: number,
): number[] {
  const start = topo.offsets[f];
  const size = topo.offsets[f + 1] - start;
  const neighbors: number[] = [];
  const seen = new Set<number>();
  seen.add(f);
  for (let j = 0; j < size; j++) {
    const ei = topo.cornerEdges[start + j];
    const fIndices = edgeFaces(cache, ei);
    for (let k = 0; k < fIndices.length; k++) {
      if (!seen.has(fIndices[k])) {
        seen.add(fIndices[k]);
        neighbors.push(fIndices[k]);
      }
    }
  }
  return neighbors;
}

// ---------------------------------------------------------------------------
// Neighbor lookup type for BFS functions
// ---------------------------------------------------------------------------

/** Callback returning an iterable of neighbor indices for BFS traversal. */
export type NeighborLookup = (index: number) => Iterable<number> | undefined;

/**
 * Edge indices adjacent to edge e (share at least one vertex).
 * Built from the CSR cache on the fly.
 */
export function edgeNeighbors(
  cache: AdjacencyCache,
  topo: PolygonTopology,
  e: number,
): number[] {
  const v0 = topo.edges[e * 2],
    v1 = topo.edges[e * 2 + 1];
  const neighbors = new Set<number>();
  const e0 = vertexEdges(cache, v0);
  for (let i = 0; i < e0.length; i++) if (e0[i] !== e) neighbors.add(e0[i]);
  const e1 = vertexEdges(cache, v1);
  for (let i = 0; i < e1.length; i++) if (e1[i] !== e) neighbors.add(e1[i]);
  return [...neighbors];
}

// ---------------------------------------------------------------------------
// Shortest path (BFS)
// ---------------------------------------------------------------------------

/**
 * Find the shortest topological path between any vertex in `sourceGroup`
 * and any vertex in `targetGroup` using BFS over the adjacency graph.
 * Returns the ordered path (inclusive of endpoints), or [] if unreachable.
 */
export function findShortestPath(
  adj: NeighborLookup,
  sourceGroup: number[],
  targetGroup: number[],
): number[] {
  const getNeighbors = adj;
  const targetSet = new Set(targetGroup);
  const parent = new Map<number, number | null>();
  const queue: number[] = [];

  for (const v of sourceGroup) {
    const n = getNeighbors(v);
    if (n) {
      parent.set(v, null);
      queue.push(v);
      if (targetSet.has(v)) return [v]; // source IS target
    }
  }

  let head = 0;
  while (head < queue.length) {
    const v = queue[head++];
    const neighbors = getNeighbors(v);
    if (!neighbors) continue;
    for (const n of neighbors) {
      if (parent.has(n)) continue;
      parent.set(n, v);
      if (targetSet.has(n)) {
        // Reconstruct path
        const path: number[] = [n];
        let cur: number | null = n;
        // biome-ignore lint/suspicious/noAssignInExpressions: intentional traversal idiom
        while ((cur = parent.get(cur as number)!) != null) path.unshift(cur);
        return path;
      }
      queue.push(n);
    }
  }
  return [];
}

// ---------------------------------------------------------------------------
// Edge-adjacency shortest path (for edge-mode ctrl+click)
// ---------------------------------------------------------------------------

/**
 * BFS shortest path between two edges (identified by numeric edge indices).
 * Returns the ordered list of edge indices along the path, or [] if unreachable.
 */
export function findShortestEdgePath(
  adj: NeighborLookup,
  source: number,
  target: number,
): number[] {
  const getNeighbors = adj;
  if (source === target) return [source];
  if (!getNeighbors(source) || !getNeighbors(target)) return [];

  const parent = new Map<number, number | null>();
  parent.set(source, null);
  const queue = [source];
  let head = 0;

  while (head < queue.length) {
    const e = queue[head++];
    const neighbors = getNeighbors(e);
    if (!neighbors) continue;
    for (const n of neighbors) {
      if (parent.has(n)) continue;
      parent.set(n, e);
      if (n === target) {
        const path: number[] = [n];
        let cur: number | null = n;
        // biome-ignore lint/suspicious/noAssignInExpressions: intentional traversal idiom
        while ((cur = parent.get(cur as number)!) != null) path.unshift(cur);
        return path;
      }
      queue.push(n);
    }
  }
  return [];
}

// ---------------------------------------------------------------------------
// Face-adjacency shortest path (for face-mode ctrl+click)
// ---------------------------------------------------------------------------

/**
 * BFS shortest path between two faces.
 * Returns the ordered list of face indices along the path, or [] if unreachable.
 */
export function findShortestFacePath(
  adj: NeighborLookup,
  source: number,
  target: number,
): number[] {
  const getNeighbors = adj;
  if (source === target) return [source];
  if (!getNeighbors(source) || !getNeighbors(target)) return [];

  const parent = new Map<number, number | null>();
  parent.set(source, null);
  const queue = [source];
  let head = 0;

  while (head < queue.length) {
    const f = queue[head++];
    const neighbors = getNeighbors(f);
    if (!neighbors) continue;
    for (const n of neighbors) {
      if (parent.has(n)) continue;
      parent.set(n, f);
      if (n === target) {
        const path: number[] = [n];
        let cur: number | null = n;
        // biome-ignore lint/suspicious/noAssignInExpressions: intentional traversal idiom
        while ((cur = parent.get(cur as number)!) != null) path.unshift(cur);
        return path;
      }
      queue.push(n);
    }
  }
  return [];
}

// ---------------------------------------------------------------------------
// Tri-to-poly mapping
// ---------------------------------------------------------------------------

/**
 * Build a map from triangle index to polygon face index.
 * Fan triangulation of face `f` with `n` corners produces `n-2` triangles.
 */
export function buildTriToPolyMap(topo: PolygonTopology): Uint32Array {
  const { offsets } = topo;
  const faceCount = offsets.length - 1;

  let triCount = 0;
  for (let f = 0; f < faceCount; f++) {
    const size = offsets[f + 1] - offsets[f];
    if (size >= 3) triCount += size - 2;
  }

  const map = new Uint32Array(triCount);
  let ti = 0;
  for (let f = 0; f < faceCount; f++) {
    const size = offsets[f + 1] - offsets[f];
    for (let j = 0; j < size - 2; j++) {
      map[ti++] = f;
    }
  }
  return map;
}

// ---------------------------------------------------------------------------
// Selection classification
// ---------------------------------------------------------------------------

/**
 * Classify which polygon edges and faces are fully selected.
 * A polygon edge is selected when both endpoints are in the set.
 * A polygon face is selected when ALL its corner vertices are in the set.
 */
export function classifyPolySelection(
  topo: PolygonTopology,
  selected: Set<number>,
): { selectedEdges: Set<number>; selectedFaces: Set<number> } {
  const { offsets, corners } = topo;
  const faceCount = offsets.length - 1;
  const selectedEdges = new Set<number>();
  const selectedFaces = new Set<number>();

  for (let f = 0; f < faceCount; f++) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    let allSelected = true;

    for (let j = 0; j < size; j++) {
      const a = corners[start + j];
      const b = corners[start + ((j + 1) % size)];
      const sa = selected.has(a);
      const sb = selected.has(b);

      if (sa && sb) {
        const ei = findEdgeIndex(topo, a, b);
        if (ei >= 0) selectedEdges.add(ei);
      }
      if (!sa) allSelected = false;
    }
    // Check last vertex (loop above checks via `selected.has(a)` for all but
    // the case where the last vertex wasn't `a` in any iteration — but it was,
    // since j goes 0..size-1 and a = corners[start+j] covers all corners)
    if (allSelected) selectedFaces.add(f);
  }

  // Wire edges (EDGE_WIRE flag, not part of any face) — selected when both
  // endpoints are in the selection set.  Handles skin armature strands and
  // any other wire-only topology where the face loop above has 0 iterations.
  const { edges, edgeFlags } = topo;
  const edgeCount = edges.length / 2;
  for (let i = 0; i < edgeCount; i++) {
    if (edgeFlags[i] & EDGE_WIRE) {
      if (selected.has(edges[i * 2]) && selected.has(edges[i * 2 + 1])) {
        selectedEdges.add(i);
      }
    }
  }

  return { selectedEdges, selectedFaces };
}

export interface ResolvedTopologyOpSelection {
  selectedEdges: Set<number>;
  selectedFaces: Set<number>;
}

/**
 * Resolve the authoritative selection for topology-changing operators.
 *
 * Face mode should honor explicit face selection, edge mode should honor
 * explicit edge selection, and only vertex-mode style workflows should infer
 * edges/faces from the selected vertex set.
 */
export function resolveTopologyOpSelection(
  topo: PolygonTopology,
  selectedVertices: Set<number>,
  granularity: "vertex" | "edge" | "face",
  explicitEdges?: ReadonlySet<number> | null,
  explicitFaces?: ReadonlySet<number> | null,
): ResolvedTopologyOpSelection {
  const inferred = classifyPolySelection(topo, selectedVertices);
  const selectedFaces =
    explicitFaces && explicitFaces.size > 0
      ? new Set(explicitFaces)
      : inferred.selectedFaces;

  const selectedEdges =
    granularity === "edge" && explicitEdges && explicitEdges.size > 0
      ? new Set(explicitEdges)
      : inferred.selectedEdges;

  return { selectedEdges, selectedFaces };
}

// ---------------------------------------------------------------------------
// Edge hit-testing on polygon faces
// ---------------------------------------------------------------------------

/** Squared distance from point p to line segment a–b. */
function pointToSegmentDistSq(
  p: [number, number, number],
  a: [number, number, number],
  b: [number, number, number],
): number {
  const abx = b[0] - a[0],
    aby = b[1] - a[1],
    abz = b[2] - a[2];
  const apx = p[0] - a[0],
    apy = p[1] - a[1],
    apz = p[2] - a[2];
  const dot = abx * apx + aby * apy + abz * apz;
  const lenSq = abx * abx + aby * aby + abz * abz;
  const t = lenSq > 0 ? Math.max(0, Math.min(1, dot / lenSq)) : 0;
  const cx = a[0] + t * abx - p[0];
  const cy = a[1] + t * aby - p[1];
  const cz = a[2] + t * abz - p[2];
  return cx * cx + cy * cy + cz * cz;
}

/**
 * Find the closest polygon edge on a given polygon face to a point.
 */
export function closestEdgeOnPolyFace(
  vertices: Float32Array,
  topo: PolygonTopology,
  polyFaceIndex: number,
  point: [number, number, number],
): { edgeIndex: number; v0: number; v1: number } {
  const start = topo.offsets[polyFaceIndex];
  const size = topo.offsets[polyFaceIndex + 1] - start;

  let bestDist = Infinity;
  let bestV0 = topo.corners[start];
  let bestV1 = topo.corners[start + 1];

  for (let j = 0; j < size; j++) {
    const v0 = topo.corners[start + j];
    const v1 = topo.corners[start + ((j + 1) % size)];
    const dist = pointToSegmentDistSq(
      point,
      [vertices[v0 * 3], vertices[v0 * 3 + 1], vertices[v0 * 3 + 2]],
      [vertices[v1 * 3], vertices[v1 * 3 + 1], vertices[v1 * 3 + 2]],
    );
    if (dist < bestDist) {
      bestDist = dist;
      bestV0 = v0;
      bestV1 = v1;
    }
  }

  return {
    edgeIndex: findEdgeIndex(topo, bestV0, bestV1),
    v0: bestV0,
    v1: bestV1,
  };
}

/**
 * Find the closest polygon edge to a point, searching the hit face and
 * all faces sharing a vertex with it. This creates an effective "fat"
 * hit zone for edges without needing additional geometry.
 */
export function closestEdgeNearFace(
  vertices: Float32Array,
  topo: PolygonTopology,
  polyFaceIndex: number,
  point: [number, number, number],
): { edgeIndex: number; v0: number; v1: number; faceIndex: number } {
  // Collect the hit face + all faces sharing at least one vertex
  const facesToSearch = new Set<number>();
  facesToSearch.add(polyFaceIndex);
  const hitStart = topo.offsets[polyFaceIndex];
  const hitSize = topo.offsets[polyFaceIndex + 1] - hitStart;
  const hitVerts = new Set<number>();
  for (let j = 0; j < hitSize; j++) hitVerts.add(topo.corners[hitStart + j]);

  // Scan all faces for vertex overlap (cheaper than building full adjacency)
  const faceCount = topo.offsets.length - 1;
  for (let f = 0; f < faceCount; f++) {
    if (f === polyFaceIndex) continue;
    const fStart = topo.offsets[f];
    const fSize = topo.offsets[f + 1] - fStart;
    for (let j = 0; j < fSize; j++) {
      if (hitVerts.has(topo.corners[fStart + j])) {
        facesToSearch.add(f);
        break;
      }
    }
  }

  // Search all edges across these faces
  let bestDist = Infinity;
  let bestV0 = topo.corners[hitStart];
  let bestV1 = topo.corners[hitStart + 1];
  let bestFace = polyFaceIndex;
  const seen = new Set<number>();

  for (const fi of facesToSearch) {
    const start = topo.offsets[fi];
    const size = topo.offsets[fi + 1] - start;
    for (let j = 0; j < size; j++) {
      const v0 = topo.corners[start + j];
      const v1 = topo.corners[start + ((j + 1) % size)];
      const edgeKey = numEdgeKey(v0, v1);
      if (seen.has(edgeKey)) continue;
      seen.add(edgeKey);
      const dist = pointToSegmentDistSq(
        point,
        [vertices[v0 * 3], vertices[v0 * 3 + 1], vertices[v0 * 3 + 2]],
        [vertices[v1 * 3], vertices[v1 * 3 + 1], vertices[v1 * 3 + 2]],
      );
      if (dist < bestDist) {
        bestDist = dist;
        bestV0 = v0;
        bestV1 = v1;
        bestFace = fi;
      }
    }
  }

  return {
    edgeIndex: findEdgeIndex(topo, bestV0, bestV1),
    v0: bestV0,
    v1: bestV1,
    faceIndex: bestFace,
  };
}

// ---------------------------------------------------------------------------
// Snap helpers — vertex, edge, and face snap target acquisition
// ---------------------------------------------------------------------------

/**
 * Find the closest vertex on a polygon face to a 3D point.
 * Returns the vertex index and its position.
 */
export function closestVertexOnFace(
  vertices: Float32Array,
  topo: PolygonTopology,
  faceIndex: number,
  point: [number, number, number],
): { index: number; position: [number, number, number] } {
  const start = topo.offsets[faceIndex];
  const end = topo.offsets[faceIndex + 1];
  let bestDist = Infinity;
  let bestIdx = topo.corners[start];
  for (let i = start; i < end; i++) {
    const vi = topo.corners[i];
    const dx = vertices[vi * 3] - point[0];
    const dy = vertices[vi * 3 + 1] - point[1];
    const dz = vertices[vi * 3 + 2] - point[2];
    const d = dx * dx + dy * dy + dz * dz;
    if (d < bestDist) {
      bestDist = d;
      bestIdx = vi;
    }
  }
  return {
    index: bestIdx,
    position: [
      vertices[bestIdx * 3],
      vertices[bestIdx * 3 + 1],
      vertices[bestIdx * 3 + 2],
    ],
  };
}

/** Compute the centroid of a polygon face. */
export function faceCentroid(
  vertices: Float32Array,
  topo: PolygonTopology,
  faceIndex: number,
): [number, number, number] {
  const start = topo.offsets[faceIndex];
  const end = topo.offsets[faceIndex + 1];
  let x = 0,
    y = 0,
    z = 0;
  const count = end - start;
  for (let i = start; i < end; i++) {
    const vi = topo.corners[i];
    x += vertices[vi * 3];
    y += vertices[vi * 3 + 1];
    z += vertices[vi * 3 + 2];
  }
  return [x / count, y / count, z / count];
}

/**
 * Closest point on line segment a→b to point p.
 * Returns the projected point and the parametric t ∈ [0, 1].
 */
export function closestPointOnEdge(
  a: [number, number, number],
  b: [number, number, number],
  p: [number, number, number],
): { point: [number, number, number]; t: number } {
  const abx = b[0] - a[0],
    aby = b[1] - a[1],
    abz = b[2] - a[2];
  const apx = p[0] - a[0],
    apy = p[1] - a[1],
    apz = p[2] - a[2];
  const dot = abx * apx + aby * apy + abz * apz;
  const lenSq = abx * abx + aby * aby + abz * abz;
  const t = lenSq > 0 ? Math.max(0, Math.min(1, dot / lenSq)) : 0;
  return {
    point: [a[0] + t * abx, a[1] + t * aby, a[2] + t * abz],
    t,
  };
}

// ---------------------------------------------------------------------------
// Topology mutation helpers (return new topologies — never mutate)
// ---------------------------------------------------------------------------

/**
 * Remove deleted vertices from a flat position buffer and produce an
 * old→new index remap table.  `remap[i]` is the new index for vertex
 * `i`, or -1 if it was deleted.
 */
export function compactVertices(
  vertices: Float32Array,
  toDelete: Set<number>,
): { vertices: Float32Array; remap: Int32Array } {
  const vertexCount = vertices.length / 3;
  const remap = new Int32Array(vertexCount).fill(-1);
  let newIdx = 0;
  for (let i = 0; i < vertexCount; i++) {
    if (!toDelete.has(i)) remap[i] = newIdx++;
  }
  const out = new Float32Array(newIdx * 3);
  for (let i = 0; i < vertexCount; i++) {
    const ni = remap[i];
    if (ni < 0) continue;
    out[ni * 3] = vertices[i * 3];
    out[ni * 3 + 1] = vertices[i * 3 + 1];
    out[ni * 3 + 2] = vertices[i * 3 + 2];
  }
  return { vertices: out, remap };
}

/**
 * Remap topology corner indices through a remap table and rebuild the
 * topology, dropping invalid faces and remapping loose edges.
 *
 * - Faces where any corner maps to -1 are removed.
 * - When `deduplicateCorners` is true, consecutive duplicate corners
 *   within a face are collapsed. Faces with fewer than 3 corners, with
 *   any remaining repeated vertex, or whose sorted vertex set duplicates
 *   an already-emitted face are dropped (needed after merge operations).
 * - Loose edges with any endpoint at -1 or both endpoints equal are dropped.
 */
export function remapTopologyCorners(
  topo: PolygonTopology,
  remap: Int32Array,
  opts?: { deduplicateCorners?: boolean },
): PolygonTopology {
  if (import.meta.env.DEV) {
    // Verify all corner indices are within the remap table range
    for (let i = 0; i < topo.corners.length; i++) {
      if (topo.corners[i] >= remap.length) {
        throw new Error(
          `remapTopologyCorners: corner[${i}] = ${topo.corners[i]} >= remap.length ${remap.length}`,
        );
      }
    }
  }
  const { offsets, corners } = topo;
  const faceCount = offsets.length - 1;
  const dedup = opts?.deduplicateCorners ?? false;

  const newCorners: number[] = [];
  const newOffsets: number[] = [0];
  const faceKeys = dedup ? new Set<string>() : null;

  for (let f = 0; f < faceCount; f++) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    let anyDeleted = false;

    for (let j = 0; j < size; j++) {
      if (remap[corners[start + j]] < 0) {
        anyDeleted = true;
        break;
      }
    }
    if (anyDeleted) continue;

    if (dedup) {
      const remapped: number[] = [];
      for (let j = 0; j < size; j++) {
        const ni = remap[corners[start + j]];
        if (remapped.length === 0 || remapped[remapped.length - 1] !== ni) {
          remapped.push(ni);
        }
      }
      if (
        remapped.length > 1 &&
        remapped[0] === remapped[remapped.length - 1]
      ) {
        remapped.pop();
      }
      if (remapped.length < 3) continue;

      const seen = new Set<number>();
      let hasRepeatedVertex = false;
      for (const vi of remapped) {
        if (seen.has(vi)) {
          hasRepeatedVertex = true;
          break;
        }
        seen.add(vi);
      }
      if (hasRepeatedVertex) continue;

      const faceKey = [...remapped].sort((a, b) => a - b).join(",");
      if (faceKeys?.has(faceKey)) continue;
      faceKeys?.add(faceKey);

      for (const vi of remapped) newCorners.push(vi);
      newOffsets.push(newCorners.length);
    } else {
      for (let j = 0; j < size; j++) {
        newCorners.push(remap[corners[start + j]]);
      }
      newOffsets.push(newCorners.length);
    }
  }

  const outOffsets = new Uint32Array(newOffsets);
  const outCorners = new Uint32Array(newCorners);
  return rebuildTopologyEdgesPreservingWire(
    outOffsets,
    outCorners,
    topo,
    (v) => remap[v],
  );
}

/**
 * Remove faces that reference any deleted vertex, then remap surviving
 * corner indices through the provided remap table.
 * `remap[oldIndex]` = newIndex, or -1 if deleted.
 */
export function deleteTopologyFaces(
  topo: PolygonTopology,
  remap: Int32Array,
): PolygonTopology {
  return remapTopologyCorners(topo, remap);
}

/**
 * Remove unreferenced vertices from a mesh and compact the buffers.
 * Returns the compacted vertices, remapped topology, and the remap table.
 */
export function compactUnreferencedVertices(
  vertices: Float32Array,
  topology: PolygonTopology,
): { vertices: Float32Array; topology: PolygonTopology; remap: Int32Array } {
  const totalVerts = vertices.length / 3;
  const used = new Uint8Array(totalVerts);
  for (let i = 0; i < topology.edges.length; i++) used[topology.edges[i]] = 1;

  const remap = new Int32Array(totalVerts).fill(-1);
  let compactCount = 0;
  for (let i = 0; i < totalVerts; i++) {
    if (used[i]) remap[i] = compactCount++;
  }

  if (compactCount === totalVerts) {
    return { vertices, topology, remap };
  }

  const compactVerts = new Float32Array(compactCount * 3);
  for (let i = 0; i < totalVerts; i++) {
    if (remap[i] >= 0) {
      compactVerts[remap[i] * 3] = vertices[i * 3];
      compactVerts[remap[i] * 3 + 1] = vertices[i * 3 + 1];
      compactVerts[remap[i] * 3 + 2] = vertices[i * 3 + 2];
    }
  }

  const compactedTopo = remapTopologyCorners(topology, remap);
  return { vertices: compactVerts, topology: compactedTopo, remap };
}

/**
 * Concatenate two topologies. Corners in `extra` are offset by `vertexOffset`.
 */
export function concatTopology(
  base: PolygonTopology,
  extra: PolygonTopology,
  vertexOffset: number,
): PolygonTopology {
  if (import.meta.env.DEV) {
    if (!Number.isInteger(vertexOffset) || vertexOffset < 0) {
      throw new Error(`concatTopology: invalid vertexOffset ${vertexOffset}`);
    }
  }
  const baseFaces = base.offsets.length - 1;
  const extraFaces = extra.offsets.length - 1;
  const baseCornerCount = base.corners.length;

  const offsets = new Uint32Array(baseFaces + extraFaces + 1);
  offsets.set(base.offsets);
  for (let f = 0; f <= extraFaces; f++) {
    offsets[baseFaces + f] = baseCornerCount + extra.offsets[f];
  }

  const corners = new Uint32Array(baseCornerCount + extra.corners.length);
  corners.set(base.corners);
  for (let i = 0; i < extra.corners.length; i++) {
    corners[baseCornerCount + i] = extra.corners[i] + vertexOffset;
  }

  // Merge edges: offset extra's vertex indices and edge indices
  const baseEdgeCount = base.edges.length / 2;
  const extraEdgeCount = extra.edges.length / 2;
  const edges = new Uint32Array(base.edges.length + extra.edges.length);
  edges.set(base.edges);
  for (let i = 0; i < extra.edges.length; i++) {
    edges[base.edges.length + i] = extra.edges[i] + vertexOffset;
  }

  // Merge cornerEdges: offset extra's edge indices
  const cornerEdges = new Uint32Array(
    baseCornerCount + extra.cornerEdges.length,
  );
  cornerEdges.set(base.cornerEdges);
  for (let i = 0; i < extra.cornerEdges.length; i++) {
    cornerEdges[baseCornerCount + i] = extra.cornerEdges[i] + baseEdgeCount;
  }

  // Merge edgeFlags
  const edgeFlags = new Uint8Array(baseEdgeCount + extraEdgeCount);
  edgeFlags.set(base.edgeFlags);
  edgeFlags.set(extra.edgeFlags, baseEdgeCount);

  // Merge edgeCreases — both halves use the SAME output edge index space
  // (base occupies [0, baseEdgeCount), extra occupies
  // [baseEdgeCount, baseEdgeCount + extraEdgeCount)) so a straight
  // concatenation is correct. Omit the field when neither side carries
  // any non-zero weight.
  let edgeCreases: Float32Array | undefined;
  if (base.edgeCreases || extra.edgeCreases) {
    const merged = new Float32Array(baseEdgeCount + extraEdgeCount);
    if (base.edgeCreases) merged.set(base.edgeCreases);
    if (extra.edgeCreases) merged.set(extra.edgeCreases, baseEdgeCount);
    let anyNonZero = false;
    for (let i = 0; i < merged.length; i++) {
      if (merged[i] !== 0) {
        anyNonZero = true;
        break;
      }
    }
    if (anyNonZero) edgeCreases = merged;
  }

  return {
    offsets,
    corners,
    edges,
    cornerEdges,
    edgeFlags,
    ...(edgeCreases ? { edgeCreases } : {}),
  };
}

/**
 * Append a single polygon face to an existing topology.
 *
 * Wire edges from the source topology are preserved — appending a face
 * should never silently destroy unrelated wire connectivity. Vertex
 * indices are unchanged, so the identity remap applies.
 */
export function appendFace(
  topo: PolygonTopology,
  faceVerts: number[],
): PolygonTopology {
  const oldFaces = topo.offsets.length - 1;
  const offsets = new Uint32Array(oldFaces + 2);
  offsets.set(topo.offsets);
  offsets[oldFaces + 1] = topo.corners.length + faceVerts.length;

  const corners = new Uint32Array(topo.corners.length + faceVerts.length);
  corners.set(topo.corners);
  corners.set(faceVerts, topo.corners.length);

  return rebuildTopologyEdgesPreservingWire(
    offsets,
    corners,
    topo,
    identityRemap,
  );
}

/**
 * Remap corner vertex indices in a topology.
 * `remapFn(oldIndex)` returns the new index.
 */
export function remapTopology(
  topo: PolygonTopology,
  remapFn: (vi: number) => number,
): PolygonTopology {
  const out = cloneTopology(topo);
  const corners = new Uint32Array(topo.corners.length);
  for (let i = 0; i < topo.corners.length; i++) {
    corners[i] = remapFn(topo.corners[i]);
  }
  const edges = new Uint32Array(topo.edges.length);
  for (let i = 0; i < topo.edges.length; i++) {
    edges[i] = remapFn(topo.edges[i]);
  }
  out.corners = corners;
  out.edges = edges;
  return out;
}

/**
 * Extract a sub-topology containing every topological element that lies
 * within the given vertex set. That means:
 *
 *   1. Faces whose corners are ALL in the set — extracted as full polygons.
 *   2. Any other edge in the source topology (face-bound or `EDGE_WIRE`)
 *      whose BOTH endpoints are in the set — extracted as a wire edge in
 *      the result. This covers selections like edge loops, where no owning
 *      face is fully selected but the user clearly expects the connecting
 *      edges to travel with the selection (matching Blender's Shift+D).
 *
 * Corner and edge vertex indices are remapped through `remap`.
 *
 * Wire-edge carry-over is handled by the shared
 * `rebuildTopologyEdgesPreservingWire` helper so every op that transforms
 * an existing topology gets the same treatment.
 */
export function extractSubTopology(
  topo: PolygonTopology,
  vertexSet: Set<number>,
  remap: Map<number, number>,
): PolygonTopology {
  const { offsets, corners } = topo;
  const faceCount = offsets.length - 1;

  const newCorners: number[] = [];
  const newOffsets: number[] = [0];

  for (let f = 0; f < faceCount; f++) {
    const start = offsets[f];
    const size = offsets[f + 1] - start;
    let allIn = true;

    for (let j = 0; j < size; j++) {
      if (!vertexSet.has(corners[start + j])) {
        allIn = false;
        break;
      }
    }

    if (allIn) {
      for (let j = 0; j < size; j++) {
        newCorners.push(remap.get(corners[start + j])!);
      }
      newOffsets.push(newCorners.length);
    }
  }

  // rebuildTopologyEdgesPreservingWire returns a full PolygonTopology that
  // already carries every per-edge attribute the source had (edgeFlags,
  // edgeCreases, future fields) — return it directly instead of
  // reassembling and silently dropping optionals.
  return rebuildTopologyEdgesPreservingWire(
    new Uint32Array(newOffsets),
    new Uint32Array(newCorners),
    topo,
    (v) => (vertexSet.has(v) ? remap.get(v)! : -1),
  );
}
